import { LinkedComponent } from '@ardoq/api-types';
import {
  getAddToMaps,
  isArdoqError,
  partialPayload,
  pipeReducers,
} from '@ardoq/common-helpers';
import { ReferenceGraphData, SimpleReferenceModel } from '@ardoq/data-model';
import { MetaModelLoadedState } from 'architectureModel/types';
import type { MetaModel } from '@ardoq/api-types';
import type { Locale } from '@ardoq/locale';
import type { DisableableDirectedTripleWithFilters } from '../types';
import { componentTypeHierarchyOperations } from 'streams/componentTypeHierarchies/componentTypeHierarchyOperations';
import { metaModelOperations } from 'architectureModel/metaModelOperations';
import { identity } from 'lodash';
import { RawGraphEdges, TraversalBuilderState } from '../types';
import {
  updateEdgesToBeRemovedOnCollapsing,
  updatePathCollapsingRules,
} from './pathCollapsing';
import { applyStartSetExpansion } from './applyStartSetExpansion';
import { getTriplesFromPathsWithoutHierarchyHops } from './getTriplesFromPathsWithoutHierarchyHops';
import { setTraversal, updateTripleOptions } from './setTraversal';
import { setStartTypeAndTraversalStartSetOrCount } from './startContext';

export const getTriples = (referenceMap: Map<string, SimpleReferenceModel>) =>
  new Map(
    Array.from(referenceMap).map(([referenceId, { sourceId, targetId }]) => [
      referenceId,
      { sourceId, targetId, referenceId },
    ])
  );

export const getBaseGraph = (
  rawGraphEdges: RawGraphEdges
): Omit<ReferenceGraphData, 'referenceTypes'> => {
  const sourceMap = new Map<string, LinkedComponent[]>();
  const targetMap = new Map<string, LinkedComponent[]>();
  const referenceMap = new Map<string, SimpleReferenceModel>();
  const addToMaps = getAddToMaps({ referenceMap, sourceMap, targetMap });
  Array.from(rawGraphEdges.values()).forEach(
    ({ modelId: referenceId, sourceId, targetId }) =>
      addToMaps({ referenceId, sourceId, targetId })
  );
  return { sourceMap, targetMap, referenceMap };
};

// Types a path uses may be gone from the org metamodel and must still offer
// options, so the paths' triples are folded in here. Hierarchy hops are left
// out: folded in, they would offer the sentinel as a component type.
const toMetaModelDerivedState = (
  metaModelResponse: MetaModel,
  paths: DisableableDirectedTripleWithFilters[][] | null,
  locale: Locale
) => {
  const triplesFromPathsWithoutHierarchyHops =
    getTriplesFromPathsWithoutHierarchyHops(paths ?? []);
  const metaModel = metaModelOperations.checkAndFixMetaModel(
    paths
      ? metaModelOperations.extendMetamodelWithTriples(
          metaModelResponse,
          triplesFromPathsWithoutHierarchyHops,
          locale
        )
      : metaModelResponse
  );
  const rawGraphNodes = metaModelOperations.createRawGraphNodes(metaModel);
  const rawGraphEdges = metaModelOperations.createRawGraphEdges(metaModel);
  const {
    typeNameToWorkspaceMap: componentNameAndWorkspacesIndex,
    componentTypeNameToFieldMap: componentNameAndFieldsIndex,
    referenceTypeNameToFieldMap: referenceNameAndFieldsIndex,
  } = metaModelOperations.typeNameToWorkspaceAndFieldMaps(metaModel);
  const baseGraph = getBaseGraph(rawGraphEdges);

  return {
    baseGraph,
    triples: getTriples(baseGraph.referenceMap),
    rawGraphNodes,
    rawGraphEdges,
    componentNameAndWorkspacesIndex,
    componentNameAndFieldsIndex,
    referenceNameAndFieldsIndex,
    referenceTypes: metaModel.referenceTypes,
    metaModelTriples: metaModel.triples,
  };
};

export const applyMetaModel = (
  state: TraversalBuilderState,
  metaModelResponse: MetaModelLoadedState
): TraversalBuilderState => {
  if (isArdoqError(metaModelResponse)) return state;

  return {
    ...state,
    // `setTraversal` reprocesses with it when the paths arrive afterwards.
    metaModel: metaModelResponse,
    // From the response as the backend sent it: the extension invents types
    // for endpoints the live metamodel lacks.
    componentTypeHierarchies:
      componentTypeHierarchyOperations.toComponentTypeHierarchies(
        componentTypeHierarchyOperations.toParentOfEdgesFromMetaModelTriples(
          metaModelResponse.triples
        )
      ),
    ...toMetaModelDerivedState(
      metaModelResponse,
      state.initialPaths,
      state.locale
    ),
    selectedGraph: {
      referenceMap: new Map(),
      targetMap: new Map(),
      sourceMap: new Map(),
    },
    isMetaModelLoaded: true,
  };
};

export const processMetaModel = (
  state: TraversalBuilderState,
  metaModelResponse: MetaModelLoadedState
) => {
  if (isArdoqError(metaModelResponse)) return state;

  const reduceStartTypeAndTraversalStartSetIfSet = state.startComponentType
    ? partialPayload(setStartTypeAndTraversalStartSetOrCount, {
        startType: state.startComponentType,
        traversalStartSet: state.traversalStartSet ?? undefined,
        traversalStartQuery: state.traversalStartQuery ?? null,
      })
    : identity;
  // Must stay ahead of `reducePathsIfSet`: `anyType` path endpoints resolve
  // against the hierarchy node.
  const reduceStartSetExpansionIfSet = state.startSetExpansion
    ? partialPayload(applyStartSetExpansion, state.startSetExpansion)
    : identity;
  const reducePathsIfSet = state.initialPaths
    ? partialPayload(setTraversal, {
        paths: state.initialPaths,
        filters: state.initialFilters,
      })
    : identity;
  const reduceCollapsingRulesIfSet = state.initialPathCollapsingRules
    ? partialPayload(
        updatePathCollapsingRules,
        state.initialPathCollapsingRules
      )
    : identity;

  return pipeReducers(
    state,
    partialPayload(applyMetaModel, metaModelResponse),
    reduceStartTypeAndTraversalStartSetIfSet,
    reduceStartSetExpansionIfSet,
    reducePathsIfSet,
    reduceCollapsingRulesIfSet,
    updateEdgesToBeRemovedOnCollapsing,
    updateTripleOptions
  );
};
